import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { API } from '../content/data';

const SAMPLES = [
  {
    name: 'Ananya Deshmukh',
    role: 'Flagship Learner · Digital Marketer',
    quote: 'Eighteen months ago I had a certificate. Today I have a live website, a growing channel, three client projects and evidence for all of it.',
    illustrative: true,
  },
  {
    name: 'Rohit Malhotra',
    role: 'Technical Track · Cybersecurity Analyst',
    quote: 'The portfolio defence was harder than any interview I have faced. That is exactly why interviews became easier.',
    illustrative: true,
  },
  {
    name: 'Meera Krishnan',
    role: 'Business & Creator Track · Independent Consultant',
    quote: 'I stopped pitching what I studied and started showing what I built. Clients noticed the difference immediately.',
    illustrative: true,
  },
];

const imgSrc = (s) => (s.image ? (s.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${s.image}` : s.image) : null);

export default function StoriesCarousel() {
  const [stories, setStories] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/stories`).then((r) => setStories(r.data)).catch(() => setStories([]));
  }, []);

  const list = stories && stories.length ? stories : SAMPLES;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 380, behavior: 'smooth' });

  return (
    <div data-testid="stories-carousel">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-navy/45">{list.length} journeys</span>
        <div className="flex gap-2">
          <button data-testid="stories-prev" onClick={() => scroll(-1)} aria-label="Previous stories" className="flex touch-target items-center justify-center rounded-full border border-navy/15 bg-white text-navy transition-colors hover:border-electric hover:text-electric">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button data-testid="stories-next" onClick={() => scroll(1)} aria-label="Next stories" className="flex touch-target items-center justify-center rounded-full border border-navy/15 bg-white text-navy transition-colors hover:border-electric hover:text-electric">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div ref={ref} className="no-scrollbar -mx-4 mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-2">
        {list.map((s, i) => (
          <div key={s.id || i} data-testid={`story-card-${i}`} className="flex w-[300px] shrink-0 snap-start flex-col rounded-3xl bg-navy p-7 text-white sm:w-[360px] sm:p-8">
            {imgSrc(s) ? (
              <img src={imgSrc(s)} alt={s.name} className="h-40 w-full rounded-2xl object-cover" loading="lazy" />
            ) : (
              <div className="bg-grid-dark flex h-40 w-full items-center justify-center rounded-2xl bg-navy-800">
                <span className="font-serif text-5xl italic text-gold/60">{s.name.charAt(0)}</span>
              </div>
            )}
            <Quote className="mt-6 h-5 w-5 text-gold/70" />
            <p className="mt-3 flex-1 font-serif text-base italic leading-relaxed text-white/85">“{s.quote}”</p>
            <div className="mt-6 border-t border-white/10 pt-4">
              <p className="text-sm font-semibold">{s.name}</p>
              <p className="mt-0.5 text-xs text-white/55">{s.role}</p>
              {s.illustrative && <span className="mt-3 inline-block rounded-full border border-gold/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold/70">Illustrative journey</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
